
import { EventType, User, Message } from "@/types/event";

// Mock Users
export const users: User[] = [
  {
    id: "user1",
    name: "John Smith",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=John"
  },
  {
    id: "user2",
    name: "Sarah Johnson",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah"
  },
  {
    id: "user3",
    name: "Mike Williams",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Mike"
  },
  {
    id: "user4",
    name: "Emily Davis",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Emily"
  },
  {
    id: "user5",
    name: "David Brown",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=David"
  }
];

// Helper function to get random users
const getRandomUsers = (count: number) => {
  const shuffled = [...users].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

// Get color based on group
const getColorByGroup = (group: string): string => {
  switch (group) {
    case "My Calendar":
      return "bg-pastel-yellow";
    case "Friends":
      return "bg-pastel-blue";
    case "Family":
      return "bg-pastel-red";
    case "Hobby":
      return "bg-pastel-green";
    case "Holidays":
      return "bg-pastel-purple";
    default:
      return "bg-pastel-yellow";
  }
};

// Generate event for today
const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1);
const nextWeek = new Date(today);
nextWeek.setDate(today.getDate() + 7);

// Mock Events
export const events: EventType[] = [
  {
    id: "event1",
    title: "Family Game Night",
    description: "Join us for a fun evening of board games and snacks. Don't forget to bring your favorite game!",
    startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 19, 0).toISOString(),
    endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 22, 0).toISOString(),
    location: "John's House",
    group: "Family",
    color: getColorByGroup("Family"),
    visibility: "public",
    attendees: getRandomUsers(4),
    created_by: "user1",
    messages: [
      {
        id: "message1",
        event_id: "event1",
        sender: users[0],
        text: "Looking forward to game night! Any preferences on what games to play?",
        timestamp: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 10, 15).toISOString()
      },
      {
        id: "message2",
        event_id: "event1",
        sender: users[1],
        text: "I'll bring Monopoly and some snacks!",
        timestamp: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 11, 30).toISOString()
      }
    ]
  },
  {
    id: "event2",
    title: "Weekend Hiking Trip",
    description: "Let's explore the mountain trails. Weather looks great for hiking!",
    startDate: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 9, 0).toISOString(),
    endDate: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 17, 0).toISOString(),
    location: "Mountain Park Trails",
    group: "Friends",
    color: getColorByGroup("Friends"),
    visibility: "public",
    attendees: getRandomUsers(3),
    created_by: "user2"
  },
  {
    id: "event3",
    title: "Book Club Meeting",
    description: "We'll be discussing 'The Midnight Library' by Matt Haig",
    startDate: new Date(nextWeek.getFullYear(), nextWeek.getMonth(), nextWeek.getDate(), 18, 30).toISOString(),
    endDate: new Date(nextWeek.getFullYear(), nextWeek.getMonth(), nextWeek.getDate(), 20, 0).toISOString(),
    location: "City Library",
    group: "Hobby",
    color: getColorByGroup("Hobby"),
    visibility: "public",
    attendees: getRandomUsers(5),
    created_by: "user3"
  },
  {
    id: "event4",
    title: "Project Deadline",
    startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2, 9, 0).toISOString(),
    endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2, 17, 0).toISOString(),
    group: "My Calendar",
    color: getColorByGroup("My Calendar"),
    visibility: "private",
    attendees: [users[0]],
    created_by: "user1"
  },
  {
    id: "event5",
    title: "Beach Vacation",
    description: "Annual family trip to the beach",
    startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14, 0, 0).toISOString(),
    endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 21, 23, 59).toISOString(),
    location: "Sunny Beach Resort",
    group: "Holidays",
    color: getColorByGroup("Holidays"),
    visibility: "public",
    attendees: getRandomUsers(4),
    created_by: "user4"
  },
  // Added Friends events
  {
    id: "event6",
    title: "Coffee Meetup",
    description: "Catching up over coffee",
    startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 10, 0).toISOString(),
    endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 11, 30).toISOString(),
    location: "Downtown Café",
    group: "Friends",
    color: getColorByGroup("Friends"),
    visibility: "public",
    attendees: getRandomUsers(2),
    created_by: "user2"
  },
  {
    id: "event7",
    title: "Movie Night",
    description: "Watching the latest superhero movie",
    startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3, 19, 0).toISOString(),
    endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3, 22, 0).toISOString(),
    location: "City Cinema",
    group: "Friends",
    color: getColorByGroup("Friends"),
    visibility: "public",
    attendees: getRandomUsers(4),
    created_by: "user3"
  }
];

// Generate more events for the current month
const generateMonthEvents = () => {
  const additionalEvents: EventType[] = [];
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  // Create 10 more random events
  for (let i = 0; i < 10; i++) {
    const randomDay = Math.floor(Math.random() * monthEnd.getDate()) + 1;
    const randomStartHour = Math.floor(Math.random() * 12) + 8; // 8 AM to 8 PM
    const randomDuration = Math.floor(Math.random() * 3) + 1; // 1 to 3 hours
    
    const eventDate = new Date(today.getFullYear(), today.getMonth(), randomDay);
    
    const groups = ["My Calendar", "Friends", "Family", "Hobby", "Holidays"];
    const randomGroup = groups[Math.floor(Math.random() * groups.length)];
    
    const eventTitles = [
      "Coffee Meetup",
      "Team Lunch",
      "Movie Night",
      "Dinner with Friends",
      "Yoga Class",
      "Doctor Appointment",
      "Shopping Trip",
      "Concert",
      "Birthday Party",
      "Study Group"
    ];
    
    const randomTitle = eventTitles[Math.floor(Math.random() * eventTitles.length)];
    
    additionalEvents.push({
      id: `event-random-${i}`,
      title: randomTitle,
      startDate: new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), randomStartHour, 0).toISOString(),
      endDate: new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), randomStartHour + randomDuration, 0).toISOString(),
      group: randomGroup,
      color: getColorByGroup(randomGroup),
      visibility: Math.random() > 0.3 ? "public" : "private", // 70% public, 30% private
      attendees: getRandomUsers(Math.floor(Math.random() * 4) + 1), // 1 to 5 attendees
      created_by: `user${Math.floor(Math.random() * 5) + 1}` // Random user as creator
    });
  }
  
  return additionalEvents;
};

export const allEvents = [...events, ...generateMonthEvents()];
